import { StreamService } from '../services/stream.service';
import { HasChanges } from '../services/HasChanges';
import { FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Component, OnInit } from "@angular/core";
import { first, mergeMap, map } from 'rxjs/operators';
import * as _ from 'lodash';
import { RoutingHistoryService, UtilService, UserService, TenantService } from '../services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, of } from 'rxjs';

@Component({
    template: `
        <div *ngIf="noTouching" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
            <spinner syle="padding-top: 100px;"></spinner>
        </div>
        <mat-horizontal-stepper *ngIf="!noTouching" #stepper (selectionChange)="selectedStep = $event.selectedIndex">
            <mat-step [stepControl]="filterForm">
                <ng-template matStepLabel>Content</ng-template>
                <div fxFlex style="padding-bottom: 15px;">
                    <h3>
                        Based on your keywords Attensa found {{totalItems}} items for your topic 
                        in the recommended sources. 
                    </h3>
                    <span fxFlex></span>
                    <button mat-raised-button color="primary" matStepperNext>Next</button>
                </div>
                <mat-tab-group mat-stretch-tabs>
                    <mat-tab label="Filters">
                        <div style="padding-top: 35px;"> 
                            <filter-builder [form]="filterForm" 
                                            (countChanged)="countChanged($event)"
                                            (sourcesChanged)="sourcesChanged($event)"></filter-builder>
                        </div>         
                    </mat-tab>
                    <mat-tab [label]="(totalItems || '') + ' Items'">
                        <div style="padding-top: 35px;">
                            <topic-builder-preview [itemCount]="totalItems" [form]="filterForm"></topic-builder-preview>
                        </div>
                    </mat-tab>
                    <mat-tab [label]="(sources?.length || '') + ' Sources'">
                        <topic-builder-sources [sources]="sources"></topic-builder-sources>
                    </mat-tab>
                </mat-tab-group>
            </mat-step>
            <mat-step>
                <ng-template matStepLabel>Publish</ng-template>
                <div fxFlex>
                    <button mat-raised-button color="primary" matStepperPrevious>Previous</button>
                    <span fxFlex></span>
                    <button mat-raised-button color="primary" matStepperNext>Next</button>
                </div>
                <topic-builder-publish [form]="publishForm" [selected]="selectedStep === 2" style="display: block; margin-top: 15px;"></topic-builder-publish>
            </mat-step>
            <mat-step>
                <ng-template matStepLabel>Share</ng-template>
                <div fxFlex>
                    <button mat-raised-button color="primary" matStepperPrevious>Previous</button>
                    <span fxFlex></span>
                    <span [matTooltip]="publishForm.invalid ? 'Topic name required.' : 'Save & Finish'">
                        <button mat-raised-button color="primary" (click)="save()" [disabled]="filterForm.invalid || publishForm.invalid">Save & Finish</button>
                    </span>
                </div>
                <topic-builder-share [title]="'Share Topic ' + publishForm.get('title').value" 
                                     [newUserForm]="shareNewUsersForm" 
                                     [currentUserForm]="shareCurrentUsersForm" 
                                     [selected]="selectedStep === 3"></topic-builder-share>
            </mat-step>
        </mat-horizontal-stepper>
    `
})
export class TopicBuilderComponent implements OnInit, HasChanges {
    noTouching;
    filterForm: FormGroup;
    publishForm: FormGroup;
    shareNewUsersForm: FormGroup;
    shareCurrentUsersForm: FormGroup;
    hasSaved: boolean;
    previewSelected: boolean;
    selectedStep: number;
    totalItems: number;
    sources: any;

    constructor(private route: ActivatedRoute,
                private router: Router,
                fb: FormBuilder,
                public snackBar: MatSnackBar,
                private streamService: StreamService,
                private tenantService: TenantService,
                private userService: UserService,
                private utilService: UtilService,
                private routingHistoryService: RoutingHistoryService) {
        this.filterForm = fb.group({
            isAdvanced: [false],
            basicFilterText: [''],
            matchType: [],
            basicFilterStructures: fb.array([]),
            advancedFilterTerm: [''],
            publishedWithinDays: ['']
        });
        this.publishForm = fb.group({
            published: [true],
            title: ['', Validators.required],
            description: [''],
            taxonomicMemberships: fb.array([]),
            tags: fb.array([]),
            rssEnabled: [false],
            emailPostingEnabled: [false],
            emailPrefix: [''],
            emailPostingAddress: ['']
        });
        this.shareNewUsersForm = fb.group({});
        this.shareCurrentUsersForm = fb.group({});
    }

    ngOnInit(): void {
        this.publishForm.get('title').setValue(this.route.snapshot.queryParams.name);
        this.filterForm.get('basicFilterText').setValue(this.route.snapshot.queryParams.term, { emitEvent: false });
        this.filterForm.get('matchType').setValue(this.route.snapshot.queryParams.matchType, { emitEvent: false });
        this.hasSaved = false;
        this.tenantService.getUnauthTenantInfo().pipe(first())
        .subscribe(tenant => this.publishForm.get('published').setValue(tenant.settings.autoTopicPublish === 'true', { emitEvent: false }));
    }

    isFilterable(filterTerm, basicFilterStructures): boolean {
        return filterTerm.trim().length !== 0 || basicFilterStructures.length !== 0;
    }

    save(): void {
        const filterTerm = this.streamService.basicFilterTermFromFilter(this.filterForm.value);
        const basicFilterText = this.utilService.cleanQuotes(this.filterForm.value.basicFilterText.trim());
        const basicFilterStructures = this.filterForm.value.basicFilterStructures.filter(basicStruct => basicStruct.values.length);
        const publishedDetails = this.publishForm.value;
        this.noTouching = true;
        this.streamService.saveStream({
            title: this.publishForm.get('title').value,
            type: 'COLLECTION',
            openForReading: true,
            published: publishedDetails.published,
            description: publishedDetails.description,
            taxonomicMemberships: publishedDetails.taxonomicMemberships,
            tags: publishedDetails.tags,
            rssEnabled: publishedDetails.rssEnabled,
            emailPostingEnabled: publishedDetails.emailPostingEnabled,
            settings: publishedDetails.emailPostingEnabled ? { uri: publishedDetails.emailPostingAddress } : undefined,
            subStreams: this.sources.map(aggr => ({ targetStreamId: aggr.key, ownerStreamId: null }))
        })
        .pipe(
            mergeMap(stream => forkJoin([
                of(stream),
                this.streamService.setSubscription(stream.id, this.userService.getCurrentUser().id, true, false, false),
                this.isFilterable(filterTerm, basicFilterStructures) ? this.streamService.postFilter(stream.id, { 
                    version: '2',
                    isBasic: true, 
                    filterTerm: filterTerm?.trim().length ? filterTerm : undefined,
                    matchType: this.filterForm.value.matchType,
                    basicFilterText: basicFilterText?.trim().length ? basicFilterText : undefined,
                    basicFilterStructures: basicFilterStructures
                }) : of(null)
            ])),
            map(([stream]) => {
                const externalInvites = (<FormArray>this.shareNewUsersForm.get('invitations')).controls.map(control => control.valid ? control.value : undefined).filter(invite => !!invite);
                if (externalInvites.length) {
                    this.userService.sendTopicInvitation(stream.id, stream.title, externalInvites).pipe(first()).subscribe();
                }
                this.userService.sendTopicInvitation(stream.id, stream.title, this.shareCurrentUsersForm.get('invitations').value.filter(invite => invite.selected)).pipe(first()).subscribe();
                return stream;
            }),
            first()
        )
        .subscribe(
            (stream) => {
                this.hasSaved = true;
                this.routingHistoryService.injectSyntheticHistoryItem(`/browse/categories/uncategorized/topics/${stream.id}`);
                this.router.navigate(['/digest', 'categories', 'uncategorized', 'topics', stream.id]);
            },
            () => {
                this.noTouching = false;
                this.snackBar.open('Unable to create topic.', 'OK', { duration: 3000});
            }
        )
    }

    hasChanges(): boolean {
        return !this.hasSaved;
    }

    existingChangesMessage(): string {
        return 'Continue and lose changes?';
    }

    countChanged(count): void {
        this.totalItems = count;
    }

    sourcesChanged(sources): void {
        this.sources = sources;
    }
}